"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { Sparkles, Brain, X, Send, Bot, User, Zap } from "lucide-react"
import { aiService } from "@/hooks/api/aiService"
import { useStudentSummary, buildStudentContextPrompt } from "@/hooks/useStudentSummary"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChatMessage {
  id: string
  type: "user" | "ai"
  content: string
  timestamp: string // ISO string so it survives JSON serialisation
}

interface WidgetSession {
  id: string
  source: "widget"
  messages: ChatMessage[]
  updatedAt: string
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

function makeId() { return `w_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` }

function loadWidgetSession(): WidgetSession | null {
  const chats: any[] = StorageService.read("aiChats") ?? []
  return chats.find((c) => c.source === "widget") ?? null
}

function saveWidgetSession(session: WidgetSession) {
  const chats: any[] = StorageService.read("aiChats") ?? []
  const idx = chats.findIndex((c) => c.source === "widget")
  if (idx === -1) chats.push(session)
  else chats[idx] = session
  StorageService.write("aiChats", chats)
}

function getAIModel(): "haiku" | "sonnet" {
  const currentUser = AuthStore.getState().currentUser
  const students: any[] = StorageService.read("students") ?? []
  const student = students.find(
    (s) => s.userId === currentUser?._id || s.email === currentUser?.email
  )
  return student?.preferences?.aiModel ?? "haiku"
}

// ─── Component ───────────────────────────────────────────────────────────────

export function AIChatBox() {
  const summary = useStudentSummary()

  // Rebuild system prompt on every render so it's always fresh
  const systemPromptRef = useRef<string>(
    "You are a helpful AI study companion. Be warm, encouraging, and concise."
  )

  const [isExpanded, setIsExpanded] = useState(false)
  const [model, setModel] = useState<"haiku" | "sonnet">("haiku")
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const historyRef = useRef<Array<{ role: "user" | "assistant"; content: string }>>([])
  const sessionRef = useRef<WidgetSession | null>(null)
  const chatInputRef = useRef<HTMLInputElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // ── Initialise from storage once ────────────────────────────────────────────
  useEffect(() => {
    const saved = loadWidgetSession()
    const aiModel = getAIModel()
    setModel(aiModel)

    const welcomeContent = summary
      ? `Hi ${summary.name}! I'm your AI Companion. What would you like to work on today?`
      : "Hello! I'm your AI Companion. How can I help you learn today?"

    if (saved && saved.messages.length > 0) {
      setMessages(saved.messages)
      sessionRef.current = saved
      historyRef.current = saved.messages
        .slice(-10)
        .map((m) => ({ role: m.type === "user" ? "user" : "assistant", content: m.content }))
    } else {
      const welcome: ChatMessage = {
        id: makeId(),
        type: "ai",
        content: welcomeContent,
        timestamp: new Date().toISOString(),
      }
      const newSession: WidgetSession = {
        id: makeId(),
        source: "widget",
        messages: [welcome],
        updatedAt: new Date().toISOString(),
      }
      setMessages([welcome])
      sessionRef.current = newSession
      historyRef.current = [{ role: "assistant", content: welcomeContent }]
      saveWidgetSession(newSession)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Rebuild system prompt when summary changes or panel opens ───────────────
  useEffect(() => {
    if (summary) {
      systemPromptRef.current = buildStudentContextPrompt(summary)
      setModel(getAIModel())
    }
  }, [summary, isExpanded])

  // ── Focus on expand ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isExpanded) setTimeout(() => chatInputRef.current?.focus(), 100)
  }, [isExpanded])

  // ── Click-outside close ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (chatContainerRef.current && !chatContainerRef.current.contains(e.target as Node)) {
        setIsExpanded(false)
      }
    }
    if (isExpanded) document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [isExpanded])

  // ── Escape close ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setIsExpanded(false) }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [])

  // ── Auto-scroll ───────────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isLoading])

  // ── Persist messages to storage ──────────────────────────────────────────────
  const persistMessages = useCallback((msgs: ChatMessage[]) => {
    const session: WidgetSession = {
      id: sessionRef.current?.id ?? makeId(),
      source: "widget",
      messages: msgs,
      updatedAt: new Date().toISOString(),
    }
    sessionRef.current = session
    saveWidgetSession(session)
  }, [])

  // ── Send message ──────────────────────────────────────────────────────────────
  const handleSend = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault()
    const text = input.trim()
    if (!text || isLoading) return
    setInput("")

    const userMsg: ChatMessage = { id: makeId(), type: "user", content: text, timestamp: new Date().toISOString() }
    const updated = [...messages, userMsg]
    setMessages(updated)
    persistMessages(updated)

    historyRef.current = [...historyRef.current, { role: "user", content: text }].slice(-10)
    setIsLoading(true)

    try {
      const reply = await aiService.chat({
        messages: historyRef.current,
        systemPrompt: systemPromptRef.current,
        model,
      })

      historyRef.current = [...historyRef.current, { role: "assistant", content: reply }].slice(-10)
      const withReply = [...updated, { id: makeId(), type: "ai" as const, content: reply, timestamp: new Date().toISOString() }]
      setMessages(withReply)
      persistMessages(withReply)
    } catch {
      const withErr = [...updated, {
        id: makeId(), type: "ai" as const,
        content: "Sorry, I couldn't connect right now. Try again in a moment.",
        timestamp: new Date().toISOString(),
      }]
      setMessages(withErr)
      persistMessages(withErr)
    } finally {
      setIsLoading(false)
    }
  }, [input, isLoading, messages, model, persistMessages])

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })

  const unread = !isExpanded && messages.filter((m) => m.type === "ai").length - 1

  return (
    <div ref={chatContainerRef} className="fixed bottom-6 right-6 z-50">
      <div className={`bg-white border border-gray-200 rounded-xl shadow-xl transition-all duration-300 ease-in-out overflow-hidden flex flex-col ${
        isExpanded ? "w-80 h-[500px]" : "w-12 h-12"
      }`}>

        {/* ── Toggle / Header ── */}
        <div className={`flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
          isExpanded ? "h-12 bg-teal-600 px-4" : "h-12 cursor-pointer bg-blue-800/50 hover:bg-blue-700/50"
        }`}>
          {isExpanded ? (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Bot className="w-5 h-5 text-white" />
                  <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-300" />
                </div>
                <div>
                  <span className="text-white font-semibold text-sm leading-none">AI Companion</span>
                  <p className="text-teal-100 text-[10px] mt-0.5 flex items-center gap-1">
                    {model === "haiku"
                      ? <><Zap className="w-2.5 h-2.5" />Haiku · Fast</>
                      : <><Sparkles className="w-2.5 h-2.5" />Sonnet · Smart</>
                    }
                  </p>
                </div>
              </div>
              <button onClick={() => setIsExpanded(false)} className="text-white/80 hover:text-white transition-colors" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button onClick={() => setIsExpanded(true)} className="w-12 h-12 flex items-center justify-center" aria-label="Open AI chat">
              <div className="relative">
                <Brain className="w-6 h-6 text-gray-50" />
                <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400" />
              </div>
            </button>
          )}
        </div>

        {/* ── Messages ── */}
        {isExpanded && (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-lg p-3 ${
                    msg.type === "user"
                      ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-br-none"
                      : "bg-white border border-gray-200 rounded-bl-none shadow-sm"
                  }`}>
                    <div className="flex items-start gap-2">
                      {msg.type === "ai" && (
                        <div className="relative flex-shrink-0 mt-0.5">
                          <Bot className="w-4 h-4 text-purple-600" />
                          <Sparkles className="absolute -top-1 -right-1 w-2 h-2 text-yellow-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${msg.type === "user" ? "text-blue-100" : "text-gray-400"}`}>
                          {formatTime(msg.timestamp)}
                        </p>
                      </div>
                      {msg.type === "user" && <User className="w-4 h-4 text-blue-200 flex-shrink-0 mt-0.5" />}
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 rounded-lg rounded-bl-none p-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Bot className="w-4 h-4 text-purple-600" />
                        <Sparkles className="absolute -top-1 -right-1 w-2 h-2 text-yellow-400" />
                      </div>
                      <div className="flex gap-1">
                        {[0, 150, 300].map((d) => (
                          <div key={d} className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* ── Input ── */}
            <div className="border-t border-gray-200 p-3 bg-white shrink-0">
              <form onSubmit={handleSend} className="flex items-center gap-2">
                <input
                  ref={chatInputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask your AI companion…"
                  disabled={isLoading}
                  className="flex-1 h-10 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all ${
                    input.trim() && !isLoading ? "bg-teal-600 text-white hover:bg-teal-700" : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
              <div className="mt-2 flex flex-wrap gap-1">
                {["Help with math", "Explain science", "Study tips", "Career advice"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setInput(s)}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Unread badge */}
      {!isExpanded && unread > 0 && (
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center pointer-events-none">
          <span className="text-xs text-white font-bold">{unread}</span>
        </div>
      )}
    </div>
  )
}
